import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  crearReceta,
  TipoProducto,
  type GuardarRecetaRequest,
} from "../api";
import Campo from "./Campo";
import BuscadorProducto from "./BuscadorProducto";

type EmisorRecetaModalProps = {
  abierto: boolean;
  onCerrar: () => void;
  mascotaID: string;
  nombreMascota: string;
  especieID?: number;
  visitaID?: string | null;
  motivoVisita?: string | null;
  diagnosticoVisita?: string | null;
};

type ItemForm = {
  productoID: number | null;
  productoNombre: string | null;
  medicamentoNombre: string;
  presentacion: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
  instrucciones: string;
};

const SUGERENCIAS_PRESENTACION = [
  "Comprimidos",
  "Gotas orales",
  "Jarabe / Suspensión",
  "Gotas óticas",
  "Colirio / Gotas oftálmicas",
  "Spray / Solución tópica",
  "Inyectable",
  "Pomada / Crema",
  "Cápsulas",
];

const SUGERENCIAS_FRECUENCIA = [
  "Cada 8 horas",
  "Cada 12 horas",
  "Cada 24 horas (una vez al día)",
  "Cada 12 horas con comida",
  "Cada 8 horas en ayunas",
  "Cada 48 horas",
  "Según necesidad / SOS",
];

const SUGERENCIAS_DURACION = [
  "Por 3 días",
  "Por 5 días",
  "Por 7 días",
  "Por 10 días",
  "Por 14 días",
  "Por 1 mes",
  "Tratamiento continuo",
];

export default function EmisorRecetaModal({
  abierto,
  onCerrar,
  mascotaID,
  nombreMascota,
  especieID = 1,
  visitaID,
  diagnosticoVisita,
}: EmisorRecetaModalProps) {
  const qc = useQueryClient();
  const navegar = useNavigate();

  const [fechaEmision, setFechaEmision] = useState(() => new Date().toISOString().slice(0, 10));
  const [validezDias, setValidezDias] = useState<number>(30);
  const [diagnostico, setDiagnostico] = useState(diagnosticoVisita ?? "");
  const [indicacionesGenerales, setIndicacionesGenerales] = useState("");
  const [errores, setErrores] = useState<Record<string, string[]>>({});

  const [items, setItems] = useState<ItemForm[]>([
    {
      productoID: null,
      productoNombre: null,
      medicamentoNombre: "",
      presentacion: "Comprimidos",
      dosis: "",
      frecuencia: "Cada 12 horas",
      duracion: "Por 7 días",
      instrucciones: "",
    },
  ]);

  const mutacionCrear = useMutation({
    mutationFn: (req: GuardarRecetaRequest) => crearReceta(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["recetas", mascotaID] });
      onCerrar();
      navegar(`/recetas/${res.recetaID}`);
    },
    onError: (err: any) => {
      if (err?.errores) {
        setErrores(err.errores);
      } else {
        setErrores({ global: [err?.message || "Ocurrió un error al emitir la receta."] });
      }
    },
  });

  if (!abierto) return null;

  const agregarItem = () => {
    setItems((prev) => [
      ...prev,
      {
        productoID: null,
        productoNombre: null,
        medicamentoNombre: "",
        presentacion: "Comprimidos",
        dosis: "",
        frecuencia: "Cada 12 horas",
        duracion: "Por 7 días",
        instrucciones: "",
      },
    ]);
  };

  const eliminarItem = (index: number) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const actualizarItem = (index: number, cambios: Partial<ItemForm>) => {
    setItems((prev) => {
      const nuevo = [...prev];
      const actual = nuevo[index];
      if (!actual) return prev;
      nuevo[index] = {
        productoID: cambios.productoID !== undefined ? cambios.productoID : actual.productoID,
        productoNombre: cambios.productoNombre !== undefined ? cambios.productoNombre : actual.productoNombre,
        medicamentoNombre: cambios.medicamentoNombre !== undefined ? cambios.medicamentoNombre : actual.medicamentoNombre,
        presentacion: cambios.presentacion !== undefined ? cambios.presentacion : actual.presentacion,
        dosis: cambios.dosis !== undefined ? cambios.dosis : actual.dosis,
        frecuencia: cambios.frecuencia !== undefined ? cambios.frecuencia : actual.frecuencia,
        duracion: cambios.duracion !== undefined ? cambios.duracion : actual.duracion,
        instrucciones: cambios.instrucciones !== undefined ? cambios.instrucciones : actual.instrucciones,
      };
      return nuevo;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrores({});

    const nuevosErrores: Record<string, string[]> = {};

    if (!fechaEmision) {
      nuevosErrores.fechaEmision = ["La fecha de emisión es obligatoria."];
    }

    if (items.length === 0) {
      nuevosErrores.items = ["Debes agregar al menos un medicamento prescrito."];
    }

    items.forEach((item, idx) => {
      const nombre = item.medicamentoNombre || item.productoNombre;
      if (!nombre?.trim()) {
        nuevosErrores[`items[${idx}].medicamentoNombre`] = ["El nombre del fármaco es obligatorio."];
      }
      if (!item.dosis?.trim()) {
        nuevosErrores[`items[${idx}].dosis`] = ["La dosis es obligatoria (ej: 1 comp, 5 ml)."];
      }
      if (!item.frecuencia?.trim()) {
        nuevosErrores[`items[${idx}].frecuencia`] = ["La frecuencia es obligatoria (ej: c/12 hrs)."];
      }
    });

    if (Object.keys(nuevosErrores).length > 0) {
      setErrores(nuevosErrores);
      return;
    }

    const payload: GuardarRecetaRequest = {
      mascotaID,
      fechaEmision,
      validezDias: Number(validezDias) || 30,
      visitaID: visitaID || null,
      diagnostico: diagnostico.trim() || null,
      indicacionesGenerales: indicacionesGenerales.trim() || null,
      items: items.map((item, idx) => ({
        productoID: item.productoID,
        medicamentoNombre: (item.medicamentoNombre || item.productoNombre || "").trim(),
        presentacion: item.presentacion.trim() || null,
        dosis: item.dosis.trim(),
        frecuencia: item.frecuencia.trim(),
        duracion: item.duracion.trim() || null,
        instrucciones: item.instrucciones.trim() || null,
        orden: idx + 1,
      })),
    };

    mutacionCrear.mutate(payload);
  };

  return (
    <div className="modal-backdrop" onClick={onCerrar}>
      <div
        className="modal-contenido modal-receta"
        style={{ maxWidth: "780px", width: "95%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-cabecera">
          <div>
            <h2>📝 Emitir Receta Médica</h2>
            <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
              Paciente: <strong>{nombreMascota}</strong>
            </p>
          </div>
          <button type="button" className="boton-cerrar" onClick={onCerrar} title="Cerrar modal">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="formulario-receta">
          {errores.global && <p className="err">{errores.global[0]}</p>}

          <div className="fila" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <Campo etiqueta="Fecha de emisión" errores={errores.fechaEmision} requerido>
              <input
                type="date"
                value={fechaEmision}
                onChange={(e) => setFechaEmision(e.target.value)}
                required
              />
            </Campo>

            <Campo etiqueta="Validez de la receta" errores={errores.validezDias}>
              <select
                value={validezDias}
                onChange={(e) => setValidezDias(Number(e.target.value))}
              >
                <option value={7}>7 días</option>
                <option value={15}>15 días</option>
                <option value={30}>30 días (1 mes)</option>
                <option value={60}>60 días (2 meses)</option>
                <option value={90}>90 días (3 meses)</option>
                <option value={180}>180 días (6 meses)</option>
              </select>
            </Campo>
          </div>

          <Campo etiqueta="Diagnóstico / Motivo de prescripción" errores={errores.diagnostico}>
            <input
              type="text"
              placeholder="Ej: Faringitis bacteriana, Control postquirúrgico, Dermatitis alérgica..."
              value={diagnostico}
              onChange={(e) => setDiagnostico(e.target.value)}
              maxLength={1000}
            />
          </Campo>

          {/* Lista de Fármacos / Medicamentos Rp. */}
          <div className="bloque-farmacos-receta" style={{ marginTop: "1rem" }}>
            <div className="titulo-fila">
              <h3 style={{ fontSize: "1.05rem", margin: 0 }}>
                💊 Medicamentos Prescritos (Rp.)
              </h3>
              <button
                type="button"
                className="boton-chico boton-primario"
                onClick={agregarItem}
              >
                ➕ Agregar fármaco
              </button>
            </div>

            <div className="lista-items-receta" style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "0.75rem" }}>
              {items.map((item, idx) => {
                const errorNombre = errores[`items[${idx}].medicamentoNombre`];
                const errorDosis = errores[`items[${idx}].dosis`];
                const errorFrecuencia = errores[`items[${idx}].frecuencia`];

                return (
                  <div
                    key={idx}
                    className="tarjeta-farmaco-receta"
                    style={{
                      border: "1px solid var(--borde)",
                      borderRadius: "0.5rem",
                      padding: "0.85rem",
                      backgroundColor: "var(--sunken)",
                      position: "relative",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                      <strong style={{ fontSize: "0.9rem", color: "var(--ink-1)" }}>
                        Fármaco #{idx + 1}
                      </strong>
                      {items.length > 1 && (
                        <button
                          type="button"
                          className="boton-icono boton-peligro"
                          onClick={() => eliminarItem(idx)}
                          title="Eliminar este fármaco"
                          style={{ padding: "0.2rem 0.5rem" }}
                        >
                          ✕ Quitar
                        </button>
                      )}
                    </div>

                    {/* Buscador o nombre libre */}
                    <div style={{ marginBottom: "0.75rem" }}>
                      <BuscadorProducto
                        tipo={TipoProducto.Medicamento}
                        especieID={especieID}
                        productoID={item.productoID}
                        productoNombre={item.productoNombre}
                        nombreLibre={item.medicamentoNombre}
                        errores={errorNombre}
                        requerido
                        onSeleccionarProducto={(prod) => {
                          actualizarItem(idx, {
                            productoID: prod?.productoID ?? null,
                            productoNombre: prod?.nombre ?? null,
                            medicamentoNombre: prod?.nombre ?? "",
                          });
                        }}
                        onCambiarNombreLibre={(texto) => {
                          actualizarItem(idx, {
                            productoID: null,
                            productoNombre: null,
                            medicamentoNombre: texto,
                          });
                        }}
                      />
                    </div>

                    {/* Presentación y Dosis */}
                    <div className="fila" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                      <Campo etiqueta="Presentación">
                        <input
                          type="text"
                          list={`presentaciones-${idx}`}
                          placeholder="Ej: Comprimidos 500mg, Jarabe..."
                          value={item.presentacion}
                          onChange={(e) => actualizarItem(idx, { presentacion: e.target.value })}
                        />
                        <datalist id={`presentaciones-${idx}`}>
                          {SUGERENCIAS_PRESENTACION.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </Campo>

                      <Campo etiqueta="Dosis" errores={errorDosis} requerido>
                        <input
                          type="text"
                          placeholder="Ej: 1/2 comprimido, 3 ml, 1 cáp..."
                          value={item.dosis}
                          onChange={(e) => actualizarItem(idx, { dosis: e.target.value })}
                          required
                        />
                      </Campo>
                    </div>

                    {/* Frecuencia y Duración */}
                    <div className="fila" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginTop: "0.5rem" }}>
                      <Campo etiqueta="Frecuencia" errores={errorFrecuencia} requerido>
                        <input
                          type="text"
                          list={`frecuencias-${idx}`}
                          placeholder="Ej: Cada 12 horas con comida..."
                          value={item.frecuencia}
                          onChange={(e) => actualizarItem(idx, { frecuencia: e.target.value })}
                          required
                        />
                        <datalist id={`frecuencias-${idx}`}>
                          {SUGERENCIAS_FRECUENCIA.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </Campo>

                      <Campo etiqueta="Duración">
                        <input
                          type="text"
                          list={`duraciones-${idx}`}
                          placeholder="Ej: Por 7 días..."
                          value={item.duracion}
                          onChange={(e) => actualizarItem(idx, { duracion: e.target.value })}
                        />
                        <datalist id={`duraciones-${idx}`}>
                          {SUGERENCIAS_DURACION.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </Campo>
                    </div>

                    {/* Instrucciones particulares */}
                    <div style={{ marginTop: "0.5rem" }}>
                      <Campo etiqueta="Instrucciones específicas / Advertencias">
                        <input
                          type="text"
                          placeholder="Ej: Administrar después de comer. No suspender antes de tiempo."
                          value={item.instrucciones}
                          onChange={(e) => actualizarItem(idx, { instrucciones: e.target.value })}
                        />
                      </Campo>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Indicaciones Generales */}
          <div style={{ marginTop: "1rem" }}>
            <Campo etiqueta="Indicaciones generales / Cuidados y medidas complementarias" errores={errores.indicacionesGenerales}>
              <textarea
                rows={3}
                placeholder="Ej: Reposo moderado, abundante agua fresca, control médico en 7 días si no hay mejoría..."
                value={indicacionesGenerales}
                onChange={(e) => setIndicacionesGenerales(e.target.value)}
                maxLength={4000}
              />
            </Campo>
          </div>

          <div className="modal-acciones" style={{ marginTop: "1.5rem", display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
            <button type="button" onClick={onCerrar} disabled={mutacionCrear.isPending}>
              Cancelar
            </button>
            <button
              type="submit"
              className="boton-primario"
              disabled={mutacionCrear.isPending}
            >
              {mutacionCrear.isPending ? "Generando receta…" : "📄 Emitir y Ver Receta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
